import { useState } from 'react';

import { api } from '../api/client';
import type { OrgUnitRow, Page, SiteRow } from '../api/types';
import { Badge, Button, Card, Field, Form, Loading, Modal, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { SiteDetail } from './SiteDetail';

/**
 * The audited things, whatever this client calls them.
 *
 * Every visible noun comes from the deployment's configuration. A build that writes
 * "School" or "Store" into its own markup is a build that serves one client, which is the
 * single thing that made SMA unsellable to a second (D1).
 */
export function Sites(): JSX.Element {
  const { can, config } = useSession();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SiteRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const query = search === '' ? '' : `?search=${encodeURIComponent(search)}`;
  const sites = useResource(() => api.get<Page<SiteRow>>(`/sites${query}`), [search]);
  const orgUnits = useResource(() => api.get<OrgUnitRow[]>('/org-units'), []);

  const unitOptions = (orgUnits.data ?? []).map((unit) => ({
    value: unit.id,
    // Indented by depth so a flat select still reads as a hierarchy.
    label: `${'— '.repeat(Math.max(0, unit.depth - 1))}${unit.name}`,
  }));

  // Drilling into one site replaces the list rather than opening beside it: sub-units and
  // attributes are a different task from browsing, and a modal over a table would make
  // both cramped.
  if (viewing !== null) {
    const site = (sites.data?.items ?? []).find((row) => row.id === viewing);
    return (
      <SiteDetail
        siteId={viewing}
        onBack={() => setViewing(null)}
        {...(can('site.edit') && site !== undefined
          ? {
              onEdit: () => {
                setViewing(null);
                setEditing(site);
              },
            }
          : {})}
      />
    );
  }

  return (
    <>
      <Card
        title={`${config.siteLabel}s`}
        actions={
          can('site.edit') ? <Button onClick={() => setCreating(true)}>Add {config.siteLabel.toLowerCase()}</Button> : undefined
        }
      >
        <div className="toolbar">
          <Field label="Search" value={search} onChange={setSearch} placeholder="Code or name" />
        </div>

        {sites.loading ? (
          <Loading />
        ) : sites.error !== null ? (
          <Notice kind="error">{sites.error}</Notice>
        ) : (
          <Table
            rows={sites.data?.items ?? []}
            keyOf={(row) => row.id}
            empty={`No ${config.siteLabel.toLowerCase()}s match.`}
            onRowClick={(row) => setViewing(row.id)}
            columns={[
              { header: 'Code', render: (row) => row.code },
              { header: 'Name', render: (row) => row.name },
              {
                header: 'Location',
                render: (row) =>
                  row.latitude === null || row.latitude === undefined
                    ? // Worth showing plainly: a site with no coordinates cannot be
                      // geofenced, so every visit to it passes the location check by default.
                      <span className="muted">Not set</span>
                    : `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}`,
              },
              {
                header: 'Geofence',
                numeric: true,
                render: (row) => (row.geofenceRadiusM === null || row.geofenceRadiusM === undefined ? '—' : `${row.geofenceRadiusM} m`),
              },
              {
                header: 'Status',
                render: (row) => (
                  <Badge tone={row.isActive ? 'accepted' : 'blocked'}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
              {
                header: '',
                render: (row) =>
                  can('site.edit') ? (
                    <Button
                      variant="secondary"
                      onClick={(event) => {
                        /*
                         * Without this the row's own click also fires, setting `viewing` —
                         * and `viewing` is checked in an early return above, so the detail
                         * screen rendered instead of the edit form and a store could not be
                         * edited at all. The old code carried this comment above an IIFE
                         * that did nothing, because Button did not pass the event through.
                         */
                        event.stopPropagation();
                        setEditing(row);
                      }}
                    >
                      Edit
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}

        {sites.data !== null && (
          <p className="muted">
            {sites.data.total} in total, page {sites.data.page} of {sites.data.totalPages}
          </p>
        )}
      </Card>

      {creating && (
        <SiteForm
          title={`Add ${config.siteLabel.toLowerCase()}`}
          unitOptions={unitOptions}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            sites.reload();
          }}
        />
      )}

      {editing !== null && (
        <SiteForm
          title={editing.name}
          site={editing}
          unitOptions={unitOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            sites.reload();
          }}
        />
      )}
    </>
  );
}

function SiteForm({
  title,
  site,
  unitOptions,
  onClose,
  onSaved,
}: {
  title: string;
  site?: SiteRow;
  unitOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [code, setCode] = useState(site?.code ?? '');
  const [name, setName] = useState(site?.name ?? '');
  const [orgUnitId, setOrgUnitId] = useState(site?.orgUnitId ?? '');
  const [latitude, setLatitude] = useState(site?.latitude ?? '');
  const [longitude, setLongitude] = useState(site?.longitude ?? '');
  const [radius, setRadius] = useState(site?.geofenceRadiusM?.toString() ?? '');
  const [address, setAddress] = useState(site?.addressLine ?? '');

  const submit = async (): Promise<void> => {
    const body = {
      name,
      orgUnitId,
      // Sent only when supplied. An empty string is not a coordinate, and sending zero
      // would place every incomplete record off the coast of Africa.
      ...(latitude === '' ? {} : { latitude: Number(latitude) }),
      ...(longitude === '' ? {} : { longitude: Number(longitude) }),
      ...(radius === '' ? {} : { geofenceRadiusM: Number(radius) }),
      ...(address === '' ? {} : { addressLine: address }),
    };

    const ok = await run(async () => {
      // Code is sent only on create. It is the stable identifier other records join on,
      // so changing it would silently re-point history at a different place.
      if (site === undefined) await api.post('/sites', { code, ...body });
      else await api.patch(`/sites/${site.id}`, body);
    });

    if (ok) onSaved();
  };

  return (
    <Modal title={title} onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Field label="Code" value={code} onChange={setCode} required hint={site === undefined ? undefined : 'Cannot be changed'} />
          <Field label="Name" value={name} onChange={setName} required />
        </div>

        <Select label="Organisation unit" value={orgUnitId} onChange={setOrgUnitId} options={unitOptions} required />

        <div className="form-row">
          <Field label="Latitude" value={String(latitude ?? '')} onChange={setLatitude} placeholder="51.5074" />
          <Field label="Longitude" value={String(longitude ?? '')} onChange={setLongitude} placeholder="-0.1278" />
          <Field
            label="Geofence radius (m)"
            value={radius}
            onChange={setRadius}
            type="number"
            hint="How close a visit must be recorded to count"
          />
        </div>

        <Field label="Address" value={address ?? ''} onChange={setAddress} />

        <div className="form-actions">
          <Button type="submit" disabled={busy || code === '' || name === '' || orgUnitId === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
